import React, { createContext, useContext, useReducer, useEffect } from "react";
import AuthService from "../services/auth.service";
import TokenManager from "../services/TokenManager";

const AuthContext = createContext();

const AUTH_ACTIONS = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  FETCH_TOKEN_START: "FETCH_TOKEN_START",
  FETCH_TOKEN_SUCCESS: "FETCH_TOKEN_SUCCESS",
  FETCH_TOKEN_FAILURE: "FETCH_TOKEN_FAILURE",
  CLEAR_ERROR: "CLEAR_ERROR",
  SET_LOADING: "SET_LOADING",
  INITIALIZE_AUTH: "INITIALIZE_AUTH",
};

const initialState = {
  token: null,
  isAuthenticated: false,
  isLoading: false,
  isFetchingToken: false,
  error: null,
  user: null,
  isInitialized: false,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        user: action.payload.user || null,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        user: null,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        user: null,
      };

    case AUTH_ACTIONS.FETCH_TOKEN_START:
      return {
        ...state,
        isFetchingToken: true,
        error: null,
      };

    case AUTH_ACTIONS.FETCH_TOKEN_SUCCESS:
      return {
        ...state,
        token: action.payload,
        isFetchingToken: false,
        error: null,
      };

    case AUTH_ACTIONS.FETCH_TOKEN_FAILURE:
      return {
        ...state,
        token: null,
        isAuthenticated: false,
        isFetchingToken: false,
        error: action.payload,
        user: null,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AUTH_ACTIONS.INITIALIZE_AUTH:
      return {
        ...state,
        isInitialized: true,
        token: action.payload.token,
        isAuthenticated: !!action.payload.token,
      };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const initializeAuth = () => {
      const token = TokenManager.retrieve();

      if (token && TokenManager.isValid(token)) {
        dispatch({
          type: AUTH_ACTIONS.INITIALIZE_AUTH,
          payload: { token },
        });
      } else if (token) {
        TokenManager.clear();
        dispatch({
          type: AUTH_ACTIONS.INITIALIZE_AUTH,
          payload: { token: null },
        });
      } else {
        dispatch({
          type: AUTH_ACTIONS.INITIALIZE_AUTH,
          payload: { token: null },
        });
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    const handleAuthLogout = (event) => {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });

      if (event.detail?.reason === "token_expired") {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: "Your session has expired. Please login again.",
        });
      }
    };

    window.addEventListener("auth:logout", handleAuthLogout);

    return () => {
      window.removeEventListener("auth:logout", handleAuthLogout);
    };
  }, []);

  const login = async (username, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const response = await AuthService.login(username, password);
      const token = response.data.access_token;

      TokenManager.store(token);
      TokenManager.storeCredentials(username, password);

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { token },
      });

      return { success: true, token };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Login failed";

      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    TokenManager.clear();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  const fetchNewToken = async () => {
    dispatch({ type: AUTH_ACTIONS.FETCH_TOKEN_START });

    try {
      const newToken = await AuthService.fetchNewToken();

      dispatch({
        type: AUTH_ACTIONS.FETCH_TOKEN_SUCCESS,
        payload: newToken,
      });

      return { success: true, token: newToken };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Token fetch failed";

      dispatch({
        type: AUTH_ACTIONS.FETCH_TOKEN_FAILURE,
        payload: errorMessage,
      });

      TokenManager.clear();

      return { success: false, error: errorMessage };
    }
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const getToken = () => {
    return state.token || TokenManager.retrieve();
  };

  const isAuthenticated = () => {
    return state.isAuthenticated && !!getToken();
  };

  const value = {
    ...state,
    login,
    logout,
    fetchNewToken,
    clearError,
    getToken,
    isAuthenticated: isAuthenticated(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
